"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, Zap, Clock, CheckCheck, ShieldCheck, Send, PhoneMissed, Bot } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = [{
  id: "instant",
  title: "Instant Response",
  description: "AI replies within seconds of a missed call.",
  icon: Zap,
  position: "top-[10%] left-[5%] md:left-[10%]",
  lineEnd: {
    x: "15%",
    y: "15%"
  },
  delay: "delay-100"
}, {
  id: "247",
  title: "24/7 Availability",
  description: "Never miss a lead, even while on the tools.",
  icon: Clock,
  position: "top-[20%] right-[5%] md:right-[10%]",
  lineEnd: {
    x: "85%",
    y: "25%"
  },
  delay: "delay-200"
}, {
  id: "capture",
  title: "Lead Captured",
  description: "Details saved directly to your unified inbox.",
  icon: ShieldCheck,
  position: "bottom-[20%] left-[5%] md:left-[15%]",
  lineEnd: {
    x: "20%",
    y: "80%"
  },
  delay: "delay-300"
}, {
  id: "admin",
  title: "Zero Admin",
  description: "Automated follow-ups and quote requests.",
  icon: Bot,
  position: "bottom-[15%] right-[5%] md:right-[15%]",
  lineEnd: {
    x: "80%",
    y: "85%"
  },
  delay: "delay-400"
}];
const messages = [{
  id: 1,
  sender: "system",
  type: "event",
  text: "Missed call from 07700 900123",
  time: "10:23 AM",
  icon: PhoneMissed
}, {
  id: 2,
  sender: "bot",
  type: "message",
  text: "Hi there! Thanks for calling ThinkLocalPro.com. We're currently on a job, but we've received your request.",
  time: "10:23 AM"
}, {
  id: 3,
  sender: "bot",
  type: "message",
  text: "Could you provide a few details about what you need? (e.g., Boiler repair, new installation). We'll get back to you with a quote ASAP.",
  time: "10:23 AM"
}, {
  id: 4,
  sender: "customer",
  type: "message",
  text: "Hi, I need a quote for a leaking radiator valve in Gloucester.",
  time: "10:25 AM"
}, {
  id: 5,
  sender: "bot",
  type: "message",
  text: "Thanks! I've logged that for the team. We'll review it and contact you shortly.",
  time: "10:25 AM"
}];
export default function MeetTheSilentOperator() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="meet-the-silent-operator" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32 radiating-pattern" ref={sectionRef}>
      {}
      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_0%,var(--background)_80%)]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-16 lg:mb-24 transition-all duration-1000 transform ${isSectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Badge variant="outline" className="mb-4 border-primary text-primary bg-primary/10 uppercase tracking-wider">
            Interactive Demo
          </Badge>
          <h2 className="h2 text-4xl md:text-5xl mb-6 text-white">
            MEET THE <span className="text-primary">SILENT OPERATOR</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            The system that sends an instant WhatsApp to your missed calls.
          </p>
        </div>

        <div className="relative w-full max-w-6xl mx-auto min-h-[800px] md:min-h-[700px] flex flex-col md:flex-row items-center justify-center">
          
          {}
          <svg className="absolute inset-0 w-full h-full z-0 hidden md:block pointer-events-none" aria-hidden="true">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.2" />
                <stop offset="50%" stopColor="var(--primary)" stopOpacity="0.8" />
                <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.2" />
              </linearGradient>
            </defs>
            {benefits.map((benefit, i) => <line key={`line-${benefit.id}`} data-index={i} x1="50%" y1="50%" x2={benefit.lineEnd.x} y2={benefit.lineEnd.y} stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="4 4" className={`transition-all duration-1000 ${isSectionVisible ? 'opacity-100' : 'opacity-0'}`} style={{
            transitionDelay: `${i * 200 + 500}ms`
          }} />)}
          </svg>

          {}
          <div className="w-full md:absolute md:inset-0 z-20 flex flex-col md:block gap-4 mb-12 md:mb-0 px-4 md:px-0">
            {benefits.map((benefit, i) => <Card key={benefit.id} data-index={i} className={`
                  md:absolute w-full md:w-[280px] glass-panel metallic-border shadow-2xl
                  transition-all duration-700 transform
                  ${benefit.position}
                  ${isSectionVisible ? `opacity-100 translate-y-0 ${benefit.delay}` : 'opacity-0 translate-y-8'}
                `}>
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                    <benefit.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-sm mb-1">{benefit.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </CardContent>
              </Card>)}
          </div>

          {}
          <div className={`
              relative z-30 w-[320px] h-[650px] bg-[#0b141a] rounded-[3rem] border-[8px] border-zinc-800 shadow-2xl overflow-hidden flex flex-col
              transition-all duration-1000 transform
              ${isSectionVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            `}>
            {}
            <div className="absolute top-0 inset-x-0 h-6 bg-zinc-800 rounded-b-3xl w-40 mx-auto z-40"></div>
            
            {}
            <div className="bg-[#202c33] pt-12 pb-3 px-4 flex items-center gap-3 z-30 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold shrink-0">
                SO
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-white text-base truncate">ThinkLocalPro.com</h3>
                <p className="text-xs text-primary flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                  Silent Operator Active
                </p>
              </div>
            </div>

            {}
            <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-4 relative z-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]">
              <div className="absolute inset-0 bg-[#0b141a]/90 z-0"></div>
              
              <div className="relative z-10 flex flex-col gap-4 w-full">
                {messages.map((msg, i) => <div key={msg.id} data-index={i} className={`flex w-full ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
                    
                    {msg.type === 'event' ? <div className="w-full flex justify-center my-2">
                        <div className="bg-[#182229] text-muted-foreground text-xs py-1 px-3 rounded-lg flex items-center gap-2 border border-border/20">
                          <msg.icon className="w-3 h-3 text-destructive" />
                          {msg.text}
                        </div>
                      </div> : <div className={`
                          max-w-[85%] rounded-2xl p-3 text-sm relative shadow-sm
                          ${msg.sender === 'customer' ? 'bg-[#005c4b] text-white rounded-tr-sm' : 'bg-[#202c33] text-zinc-200 rounded-tl-sm border border-border/10'}
                        `}>
                        <p className="leading-relaxed">{msg.text}</p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <span className="text-[10px] text-zinc-400">{msg.time}</span>
                          {msg.sender === 'customer' && <CheckCheck className="w-3 h-3 text-[#53bdeb]" />}
                        </div>
                      </div>}
                  </div>)}
              </div>
            </div>

            {}
            <div className="bg-[#202c33] p-3 flex items-center gap-2 z-30">
              <div className="flex-1 bg-[#2a3942] rounded-full h-10 px-4 flex items-center text-sm text-zinc-400">
                Type a message...
              </div>
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground shrink-0 shadow-md cursor-not-allowed">
                <Send className="w-4 h-4 ml-1" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}